/**
 * Servicio de Cambios de Fecha
 * Maneja solicitudes de cambio de fecha de cheques posfechados
 */
import api from './api';

/**
 * Solicitar cambio de fecha para un cheque
 * @param {Object} data - Datos del cambio (cheque_id, fecha_nueva, motivo, solicitante)
 * @returns {Promise<Object>} Solicitud de cambio creada
 */
export const requestDateChange = async (data) => {
  try {
    const response = await api.post('/cambios-fecha', data);
    return response.data;
  } catch (error) {
    console.error('Error al solicitar cambio de fecha:', error);
    throw error;
  }
};

/**
 * Obtener todos los cambios de fecha con filtros
 * @param {Object} filters - Filtros (estado, chequeId, clienteId, fechaDesde, fechaHasta)
 * @returns {Promise<Array>} Lista de cambios de fecha
 */
export const getDateChanges = async (filters = {}) => {
  try {
    const response = await api.get('/cambios-fecha', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener cambios de fecha:', error);
    throw error;
  }
};

/**
 * Obtener un cambio de fecha por ID
 * @param {string} id - ID del cambio de fecha
 * @returns {Promise<Object>} Datos del cambio de fecha
 */
export const getDateChangeById = async (id) => {
  try {
    const response = await api.get(`/cambios-fecha/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener cambio de fecha:', error);
    throw error;
  }
};

/**
 * Obtener cambios de fecha de un cheque específico
 * @param {string} chequeId - ID del cheque
 * @returns {Promise<Array>} Lista de cambios de fecha del cheque
 */
export const getDateChangesByCheque = async (chequeId) => {
  try {
    const response = await api.get(`/cheques/${chequeId}/cambio-fecha`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener cambios de fecha del cheque:', error);
    throw error;
  }
};

/**
 * Aprobar un cambio de fecha
 * @param {string} id - ID del cambio de fecha
 * @param {Object} data - Datos de aprobación (comentarios, etc.)
 * @returns {Promise<Object>} Cambio de fecha aprobado
 */
export const approveDateChange = async (id, data = {}) => {
  try {
    const response = await api.patch(`/cambios-fecha/${id}/aprobar`, data);
    return response.data;
  } catch (error) {
    console.error('Error al aprobar cambio de fecha:', error);
    throw error;
  }
};

/**
 * Rechazar un cambio de fecha
 * @param {string} id - ID del cambio de fecha
 * @param {Object} data - Datos de rechazo (motivo, comentarios)
 * @returns {Promise<Object>} Cambio de fecha rechazado
 */
export const rejectDateChange = async (id, data) => {
  try {
    const response = await api.patch(`/cambios-fecha/${id}/rechazar`, data);
    return response.data;
  } catch (error) {
    console.error('Error al rechazar cambio de fecha:', error);
    throw error;
  }
};

/**
 * Actualizar un cambio de fecha pendiente
 * @param {string} id - ID del cambio de fecha
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Cambio de fecha actualizado
 */
export const updateDateChange = async (id, data) => {
  try {
    const response = await api.put(`/cambios-fecha/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar cambio de fecha:', error);
    throw error;
  }
};

/**
 * Cancelar un cambio de fecha
 * @param {string} id - ID del cambio de fecha
 * @param {string} motivo - Motivo de cancelación
 * @returns {Promise<Object>} Cambio de fecha cancelado
 */
export const cancelDateChange = async (id, motivo) => {
  try {
    const response = await api.patch(`/cambios-fecha/${id}/cancelar`, { motivo });
    return response.data;
  } catch (error) {
    console.error('Error al cancelar cambio de fecha:', error);
    throw error;
  }
};

/**
 * Obtener cambios de fecha pendientes de aprobación
 * @returns {Promise<Array>} Lista de cambios pendientes
 */
export const getPendingDateChanges = async () => {
  try {
    const response = await api.get('/cambios-fecha/pendientes');
    return response.data;
  } catch (error) {
    console.error('Error al obtener cambios de fecha pendientes:', error);
    throw error;
  }
};

/**
 * Obtener historial de cambios de fecha de un cliente
 * @param {string} clienteId - ID del cliente
 * @returns {Promise<Array>} Historial de cambios de fecha
 */
export const getDateChangesByCliente = async (clienteId) => {
  try {
    const response = await api.get('/cambios-fecha', {
      params: { clienteId },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener cambios de fecha por cliente:', error);
    throw error;
  }
};

export default {
  requestDateChange,
  getDateChanges,
  getDateChangeById,
  getDateChangesByCheque,
  approveDateChange,
  rejectDateChange,
  updateDateChange,
  cancelDateChange,
  getPendingDateChanges,
  getDateChangesByCliente,
};
